import axios from 'axios';
import { clearAuthenticatedUser } from './authStorage';
import { getAPILanguage, responseData, withLanguage } from './apiResponse';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const API_TIMEOUT_MS = Math.max(1000, Number(process.env.REACT_APP_API_TIMEOUT_MS) || 30000);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Helper: check if a JWT is expired (with 60s buffer) ---
function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return false;
    return Date.now() >= (payload.exp - 60) * 1000; // 60s buffer
  } catch {
    return true; // malformed token treated as expired
  }
}

// Share one refresh request so every waiter settles on both success and failure.
let refreshPromise = null;

function clearStoredAuth() {
	clearAuthenticatedUser();
}

function refreshAccessToken(refreshToken) {
  if (!refreshPromise) {
    refreshPromise = axios.post(
      `${API_BASE_URL}/users/refresh-token`,
      { refresh_token: refreshToken },
      { timeout: API_TIMEOUT_MS, params: { lang: getAPILanguage() } }
    ).then((response) => {
      const { token: newToken, refresh_token: newRefresh } = responseData(response);
      localStorage.setItem('token', newToken);
      localStorage.setItem('refresh_token', newRefresh);
      return newToken;
    }).catch((error) => {
      clearStoredAuth();
      throw error;
    }).finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

// --- Request interceptor: attach token, proactively refresh if expired ---
api.interceptors.request.use(async (config) => {
	config = withLanguage(config);
  // Skip token for refresh-token endpoint itself to avoid loops
  if (config.url === '/users/refresh-token') {
    return config;
  }

  let userToken = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refresh_token');

  // If user token exists but is expired, try to refresh proactively
  if (userToken && isTokenExpired(userToken) && refreshToken) {
    try {
      userToken = await refreshAccessToken(refreshToken);
    } catch {
      // Refresh failed; let the request continue without stale credentials.
      userToken = null;
    }
  }

  // If user token is still expired (refresh failed), discard it
  if (userToken && isTokenExpired(userToken)) {
    clearStoredAuth();
    userToken = null;
  }

  const token = userToken;
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Response interceptor: handle 401 with refresh token retry ---
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only retry once, and not for refresh-token / login / register endpoints
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/users/refresh-token') &&
      !originalRequest.url?.includes('/users/login') &&
      !originalRequest.url?.includes('/users/register') &&
      !originalRequest.url?.includes('/users/social-login') &&
      !originalRequest.url?.includes('/users/apple-login')
    ) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        originalRequest._retry = true;

        try {
          const newToken = await refreshAccessToken(refreshToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch {
          return Promise.reject(error);
        }
      }
    }

    return Promise.reject(error);
  }
);

export const intentAPI = {
	getIntent: (requestId) => api.get(`/intents/${requestId}`),
	getIntentByLink: (linkToken) => api.get(`/intent_by_token/${encodeURIComponent(linkToken)}`),
  createIntent: (data) => api.post('/intents/create', data),
};

export const kycAPI = {
  getKycInfo: () => api.get('/kyc/info'),
  startKYC: () => api.post('/kyc/start'),
  refreshKYCToken: () => api.post('/kyc/token'),
  getKYCStatus: () => api.get('/kyc/status'),
};

export const paymentAPI = {
  createPayment: (data) => api.post('/payment/create', data),
  getPaymentStatus: (transactionId) => api.get(`/payment/status/${transactionId}`),
  checkTransaction: (transactionId) => api.get(`/payment/check/${transactionId}`),
  getUserTransactions: () => api.get('/payment/user-transactions'),
  getPaymentRequests: (params = {}) => api.get('/payment/requests', { params }),
  getPaymentRequest: (requestId) => api.get(`/payment/requests/${encodeURIComponent(requestId)}`),
  continuePaymentRequest: (requestId, data, idempotencyKey) => api.post(
    `/payment/requests/${encodeURIComponent(requestId)}/continue`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } }
  ),
  cancelPaymentAttempt: (requestId, attemptId) => api.post(
    `/payment/requests/${encodeURIComponent(requestId)}/attempts/${encodeURIComponent(attemptId)}/cancel`
  ),
};

export const transakAPI = {
  createSession: (data) => api.post('/transak/create-session', data),
  getOrder: (orderId) => api.get(`/transak/order/${orderId}`),
};

export const onrampAPI = {
  getQuotes: (data, config = {}) => api.post('/onramp/quotes', data, config),
  createSession: (data) => api.post('/onramp/sessions', data),
  notifyOrderCreated: (data) => api.post('/onramp/orders/created', data),
  notifyOrderSuccessful: (data) => api.post('/onramp/orders/successful', data),
  getOrder: (orderId, provider = null) => api.get(`/onramp/orders/${orderId}`, {
    params: provider ? { provider } : {},
  }),
  listProviders: () => api.get('/onramp/providers'),
  recommendProvider: (data) => api.post('/onramp/providers/recommendation', data),
  getBurjxReadiness: () => api.get('/onramp/providers/burjx/readiness'),
};

export const walletAPI = {
  listWallets: () => api.get('/wallets'),
  createWallet: (data = {}) => api.post('/wallets', data),
  exportPrivateKey: (walletId, code) => api.post(`/wallets/${walletId}/export`, { code }),
};

export const userAPI = {
  getIdentities: () => api.get('/users/identities'),
  sendVerificationCode: ({ identityType, identifier, requestId, linkToken }) => {
    const body = {
      identity_type: identityType,
      ...(requestId ? { request_id: requestId, link_token: linkToken } : { identifier }),
      purpose: 'login',
    };
    return api.post('/users/send-code', body);
  },
  sendLegacyVerificationCode: (email, purpose) => api.post('/users/send-code', { email, purpose }),
  register: (registrationToken) =>
    api.post('/users/register', { registration_token: registrationToken }),
  login: (challengeId, code) =>
    api.post('/users/login', { challenge_id: challengeId, code }),
  sendIdentityVerificationCode: (identityType, identifier) =>
    api.post('/users/identities/send-code', {
      identity_type: identityType,
      identifier,
    }),
  linkIdentity: (challengeId, code) =>
    api.post('/users/identities', { challenge_id: challengeId, code }),
  removeIdentity: (identityType) =>
    api.delete(`/users/identities/${encodeURIComponent(identityType)}`),
  socialLogin: (provider, token) =>
    api.post('/users/social-login', { provider, token }),
  appleLogin: (payload) =>
    api.post('/users/apple-login', payload),
  refreshToken: (refreshToken) =>
    api.post('/users/refresh-token', { refresh_token: refreshToken }),
  logout: (refreshToken) =>
    api.post('/users/logout', { refresh_token: refreshToken }),
  deleteAccount: () => api.delete('/users/me'),
  getCurrentUser: () => api.get('/users/me'),
  updateLimits: (userId, limits) =>
    api.put(`/users/limits?user_id=${userId}`, limits),
  getContacts: () => api.get('/users/contacts'),
  getContact: (contactId) => api.get(`/users/contacts/${encodeURIComponent(contactId)}`),
  createContactTransfer: (contactId) => api.post(`/users/contacts/${encodeURIComponent(contactId)}/transfer`),
};

export const countryAPI = {
  getCountries: () => api.get('/countries'),
  getCountryByAlpha2: (alpha2) => api.get(`/countries/${alpha2}`),
};

export const limitsAPI = {
  getLimits: () => api.get('/limits'),
  // updateLimit: (type, maxAmount) => api.put(`/limits/${type}`, { max_amount: maxAmount }),
};

export default api;
